import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  STATUS_TYPE,
  STATUS_TYPE_MAP,
  STATUS_TYPE_VALUE,
  statusRandomList,
} from '../simejiStatus';
import useViewport from '@hooks/useViewport';
import Draggable from 'react-draggable';
import useInterval from '@hooks/useInterval';
import Body from './DrawBody';
import DrawImageBody from './DrawImageBody';
import { ISimejiItem } from '../simejiStatus.interface';
import useSetRandomTimer from '@hooks/useSetRandomTimer';
import SimejiScript from './SimejiScript';

const BodyBox = styled.div<{ $bodyWidth: number; $bodyHeight: number }>`
  position: fixed;
  z-index: 100;
  width: ${({ $bodyWidth }) => $bodyWidth}px;
  height: ${({ $bodyHeight }) => $bodyHeight}px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

type Props = {
  data: ISimejiItem;
};

function SimejiBody({ data }: Props) {
  const isImageType = data.renderType === 'image';

  const [status, setStatus] = useState<{
    key: STATUS_TYPE_VALUE;
    value: string;
  }>({
    key: STATUS_TYPE.DEFAULT,
    value: STATUS_TYPE_MAP[STATUS_TYPE.DEFAULT],
  });

  // key: STATUS_TYPE.CLIMB,
  // value: STATUS_TYPE_MAP[STATUS_TYPE.CLIMB],
  const { viewWidth, viewHeight } = useViewport();
  const bodyWidth = isImageType ? (data.imageWidth || 100) : 100;
  const bodyHeight = isImageType ? (data.imageHeight || 120) : 120;
  const rightEdge = viewWidth - bodyWidth;
  const bottomEdge = viewHeight - bodyHeight;
  const leftCenter = viewWidth / 2 - bodyWidth / 2;
  const bottomCenter = viewHeight / 2 + bodyHeight / 2;
  const [customFace, setCustomFace] = useState<ISimejiItem>();

  const [dir, setDir] = useState<number>(0); // 0중립 / 1오른쪽 / -1왼쪽
  const [deltaPosition, setDeltaPosition] = useState({
    x: leftCenter,
    y: bottomEdge,
  });

  useSetRandomTimer({
    randomList: statusRandomList,
    setCustomStatus: setStatus,
  });

  // 랜덤 나아가는 방향 생성 ---------------------------------------------
  const getRandomDir = useCallback(() => {
    const arr = [1, -1];
    const randomIndex = Math.floor(Math.random() * arr.length);
    return arr[randomIndex];
  }, []);
  // END 랜덤 나아가는 방향 생성 ---------------------------------------------

  useInterval(() => {
    if (status.key === STATUS_TYPE.WALK) {
      setDeltaPosition((prev) => {
        return { ...prev, x: prev.x + dir };
      });
    }
  }, data.speed);

  const walk = useCallback(() => {
    if (deltaPosition.x === rightEdge) {
      // 캐릭터가 오른쪽 끝에 다다랐을때
      // 벽타기 실행
      climb();
      return;
    }

    // 랜덤방향 생성
    const _dir = getRandomDir();
    setDir(_dir);
  }, [status]);

  const climb = useCallback(() => {
    if (deltaPosition.y === 0) {
      // 캐릭터가 가장 바닥일때는 위로 올라감
      setDir(-1);
      return;
    }

    // 랜덤방향 생성
    const _dir = getRandomDir();
    setDir(_dir);
  }, []);

  useEffect(() => {
    // 상태에 따라 다르게 실행되는 로직
    switch (status.key) {
      case STATUS_TYPE.DEFAULT:
        console.log('기본실행');
        setDir(0);
        break;
      case STATUS_TYPE.WALK:
        console.log('걷기 실행');
        walk();
        break;
      case STATUS_TYPE.SLEEP:
        setDir(0);
        console.log('꾸벅꾸벅 실행');
        break;
      // case STATUS_TYPE.CLIMB:
      //   console.log("벽타기 실행");
      //   climb();
      //   break;
      // case STATUS_TYPE.EAT:
      //   setDir(0);
      //   console.log("먹기 실행");
      //   break;
      case STATUS_TYPE.MOVE:
        console.log('클릭하고 이동 시 실행');
        break;
      case STATUS_TYPE.CLICK:
        setDir(0);
        console.log('클릭 실행');
        break;
      // case STATUS_TYPE.PENDING:
      //   setDir(0);
      //   console.log("대기중");
      //   break;
      default:
        console.log(`알맞은 상태가 없습니다.`);
    }
  }, [status]);

  // click ~ drag --------------------------------------------------
  const handleMouseDown = useCallback(() => {
    setStatus({
      key: STATUS_TYPE.CLICK,
      value: STATUS_TYPE_MAP[STATUS_TYPE.CLICK],
    });
  }, []);

  const handleDrag = useCallback(
    (e, ui) => {
      setStatus({
        key: STATUS_TYPE.MOVE,
        value: STATUS_TYPE_MAP[STATUS_TYPE.MOVE],
      });

      const { x, y } = deltaPosition;
      setDeltaPosition({
        x: x + ui.deltaX,
        y: y + ui.deltaY,
      });
    },
    [deltaPosition],
  );

  const handleStop = useCallback(
    (e, ui) => {
      setStatus({
        key: STATUS_TYPE.DEFAULT,
        value: STATUS_TYPE_MAP[STATUS_TYPE.DEFAULT],
      });

      // 업데이트 후 리셋
      const { x, y } = deltaPosition;
      setDeltaPosition({
        x: x + ui.deltaX,
        y: bottomEdge,
      });
    },
    [deltaPosition],
  );

  return (
    <>
      <Draggable
        key={'id'}
        onDrag={handleDrag}
        onStart={(e) => { }}
        onStop={handleStop}
        onMouseDown={handleMouseDown}
        bounds="parent"
        defaultClassName="react-draggable"
        defaultPosition={deltaPosition}
        position={deltaPosition}
      >
        <BodyBox $bodyWidth={bodyWidth} $bodyHeight={bodyHeight}>
          {isImageType ? (
            <DrawImageBody type={status.key} dir={dir} data={data} />
          ) : (
            <Body
              type={status.key}
              dir={dir}
              data={{ ...data, ...customFace }}
            />
          )}
          <SimejiScript data={data} setCustomFace={setCustomFace} isImageType={isImageType} />
        </BodyBox>
      </Draggable>
    </>
  );
}

export default SimejiBody;
